import { useState, useRef, useCallback, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import buffer from "@turf/buffer";
import length from "@turf/length";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import lineSlice from "@turf/line-slice";
import { point, lineString } from "@turf/helpers";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { calculateAcres, calculateCenter } from "../utils/mapCalculations";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { logger } from "../../../lib/logger";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Drawing modes the admin can pick from when starting an area
export const DRAW_MODES = {
  POLYGON: "polygon",   // Click-to-place vertices, double-click to finish (Mapbox built-in)
  FREEHAND: "freehand", // Mouse-down/drag/up to capture a freehand outline
  TRAIL: "trail",       // Draw a line, automatically buffered into a polygon by width-in-feet
  PARCEL: "parcel",     // Click any parcel on the map → use its full boundary as the area
};

const NWI_TILE_URL =
  "https://fwspublicservices.wim.usgs.gov/wetlandsmapservice/rest/services/Wetlands/MapServer/export?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&layers=show:0";

const useMapbox = ({ polygons, setPolygons, propertyAddress, setPropertyAddress, setParcelPin }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showAddAreaDialog, setShowAddAreaDialog] = useState(false);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [loadingParcels, setLoadingParcels] = useState(false);
  const [showWetlands, setShowWetlands] = useState(true);
  // Drawing mode + trail-maker settings (kept in refs too, for use inside Mapbox event callbacks)
  const [drawMode, setDrawMode] = useState(DRAW_MODES.POLYGON);
  const drawModeRef = useRef(DRAW_MODES.POLYGON);
  const [trailWidthFeet, setTrailWidthFeet] = useState(20);
  const trailWidthRef = useRef(20);
  const freehandRef = useRef({ active: false, points: [], sourceId: "freehand-preview" });

  // Follow-Property-Line mode: while in POLYGON draw mode, when this toggle is ON,
  // the next click will snap the segment from the previous vertex to the parcel
  // boundary. User picks between the shorter and longer path around the ring.
  const [followParcelLine, setFollowParcelLine] = useState(false);
  const followParcelLineRef = useRef(false);
  const [boundaryChoice, setBoundaryChoice] = useState(null); // { shorter, longer, pickPath }
  const activePolygonIdRef = useRef(null); // MapboxDraw feature id of the in-progress polygon

  useEffect(() => { followParcelLineRef.current = followParcelLine; }, [followParcelLine]);

  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { trailWidthRef.current = trailWidthFeet; }, [trailWidthFeet]);

  // Convert a LineString into a width-buffered Polygon (trail maker).
  const trailLineToPolygon = useCallback((lineGeometry, widthFeet) => {
    const w = parseFloat(widthFeet) || 20;
    if (!lineGeometry || lineGeometry.type !== "LineString" || lineGeometry.coordinates.length < 2) return null;
    const line = lineString(lineGeometry.coordinates);
    // @turf/buffer uses miles/kilometers — convert feet → miles. Half-width because buffer extends outward on BOTH sides.
    const halfWidthMiles = (w / 2) / 5280;
    const buffered = buffer(line, halfWidthMiles, { units: "miles", steps: 16 });
    if (!buffered || !buffered.geometry) return null;
    return buffered.geometry;
  }, []);

  const handleDrawCreate = useCallback((e) => {
    const features = e.features;
    if (!features || features.length === 0) {
      setIsDrawing(false);
      return;
    }
    const feature = features[0];
    let polygon = null;
    const mode = drawModeRef.current;

    if (feature.geometry.type === "LineString") {
      // Trail mode — buffer the line by the configured width
      polygon = trailLineToPolygon(feature.geometry, trailWidthRef.current);
      if (!polygon) {
        toast.error("Trail must have at least 2 points");
        if (draw.current) draw.current.delete(feature.id);
        setIsDrawing(false);
        return;
      }
      // Persist trail metadata on the polygon itself so we can re-buffer it later
      // with a different width (the original line is the source of truth for buffering).
      polygon._meta = {
        kind: "trail",
        width_ft: trailWidthRef.current,
        line: feature.geometry.coordinates,
      };
      // Replace the raw line with the buffered polygon so it actually renders
      if (draw.current && feature.id) draw.current.delete(feature.id);
      if (draw.current) {
        draw.current.add({
          type: "Feature",
          properties: { __trail: true, __trail_width_ft: trailWidthRef.current },
          geometry: polygon,
        });
      }
      const lineLengthMiles = length(lineString(feature.geometry.coordinates), { units: "miles" });
      toast.success(
        `Trail buffered: ${(lineLengthMiles * 5280).toFixed(0)} ft long × ${trailWidthRef.current} ft wide`
      );
    } else if (feature.geometry.type === "Polygon") {
      polygon = feature.geometry;
      const acres = calculateAcres(polygon);
      const label = mode === DRAW_MODES.FREEHAND ? "Freehand area" : "Area";
      toast.success(`${label} drawn: ${acres.toFixed(2)} acres`);
    }

    if (polygon) {
      setPendingPolygon(polygon);
      setShowAddAreaDialog(true);
    }
    setIsDrawing(false);
  }, [trailLineToPolygon]);

  const handleDrawUpdate = useCallback(() => {
    if (!draw.current) return;
    const allFeatures = draw.current.getAll();
    const updatedPolygons = allFeatures.features
      .filter(f => f.geometry.type === "Polygon")
      .map(f => f.geometry);
    setPolygons(updatedPolygons);
  }, [setPolygons]);

  const handleDrawDelete = useCallback(() => {
    if (!draw.current) return;
    const allFeatures = draw.current.getAll();
    const remainingPolygons = allFeatures.features
      .filter(f => f.geometry.type === "Polygon")
      .map(f => f.geometry);
    setPolygons(remainingPolygons);
    toast.info("Area removed");
  }, [setPolygons]);

  const fitMapToPolygons = useCallback(() => {
    if (!map.current || polygons.length === 0) return;
    
    const allCoords = [];
    polygons.forEach(poly => {
      if (poly && poly.coordinates && poly.coordinates[0]) {
        allCoords.push(...poly.coordinates[0]);
      }
    });
    
    if (allCoords.length > 0) {
      const bounds = allCoords.reduce(
        (bounds, coord) => bounds.extend(coord),
        new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
      );
      map.current.fitBounds(bounds, { padding: 50 });
    }
  }, [polygons]);

  const fetchParcelData = useCallback(async (address) => {
    if (!address || !map.current || !mapLoaded) return;
    
    setLoadingParcels(true);
    try {
      const response = await axios.post(`${API}/parcels/by-address`, { address });
      const data = response.data;
      
      if (data.features && data.features.length > 0) {
        if (map.current.getLayer("selected-parcel-fill")) {
          map.current.removeLayer("selected-parcel-fill");
        }
        if (map.current.getLayer("selected-parcel-line")) {
          map.current.removeLayer("selected-parcel-line");
        }
        if (map.current.getLayer("neighbor-parcels-fill")) {
          map.current.removeLayer("neighbor-parcels-fill");
        }
        if (map.current.getLayer("neighbor-parcels-line")) {
          map.current.removeLayer("neighbor-parcels-line");
        }
        if (map.current.getSource("selected-parcel")) {
          map.current.removeSource("selected-parcel");
        }
        if (map.current.getSource("neighbor-parcels")) {
          map.current.removeSource("neighbor-parcels");
        }
        
        const geocoded = data.geocoded;
        let selectedParcel = null;
        const neighborParcels = [];
        
        if (geocoded && geocoded.lat && geocoded.lon) {
          const searchPoint = point([geocoded.lon, geocoded.lat]);
          
          for (const feature of data.features) {
            if (feature.geometry && feature.geometry.type === "Polygon") {
              const isInside = booleanPointInPolygon(searchPoint, feature);
              if (isInside && !selectedParcel) {
                selectedParcel = feature;
              } else {
                neighborParcels.push(feature);
              }
            }
          }
          
          if (!selectedParcel && data.features.length > 0) {
            selectedParcel = data.features[0];
            neighborParcels.length = 0;
            neighborParcels.push(...data.features.slice(1));
          }
        } else {
          selectedParcel = data.features[0];
          neighborParcels.push(...data.features.slice(1));
        }
        
        if (neighborParcels.length > 0) {
          map.current.addSource("neighbor-parcels", {
            type: "geojson",
            data: { type: "FeatureCollection", features: neighborParcels }
          });
          
          map.current.addLayer({
            id: "neighbor-parcels-fill",
            type: "fill",
            source: "neighbor-parcels",
            paint: { "fill-color": "#4169E1", "fill-opacity": 0.05 }
          });
          
          map.current.addLayer({
            id: "neighbor-parcels-line",
            type: "line",
            source: "neighbor-parcels",
            paint: { "line-color": "#4169E1", "line-width": 1.5, "line-opacity": 0.5 }
          });
        }
        
        if (selectedParcel) {
          map.current.addSource("selected-parcel", {
            type: "geojson",
            data: { type: "FeatureCollection", features: [selectedParcel] }
          });
          
          map.current.addLayer({
            id: "selected-parcel-fill",
            type: "fill",
            source: "selected-parcel",
            paint: { "fill-color": "#4169E1", "fill-opacity": 0.15 }
          });
          
          map.current.addLayer({
            id: "selected-parcel-line",
            type: "line",
            source: "selected-parcel",
            paint: { "line-color": "#4169E1", "line-width": 3.5, "line-opacity": 1.0 }
          });
          
          const props = selectedParcel.properties || {};
          const parcelStreet = props.siteadd || props.site_address || props.address || "";
          const geocodedCity = data.geocoded?.city || "";
          const geocodedState = data.geocoded?.state || "NC";
          const geocodedZip = data.geocoded?.zipcode || "";
          const pin = props.parno || props.pin || props.parcel_id || props.reid || props.parcelid || "";
          
          if (parcelStreet) {
            const addressParts = [parcelStreet.toUpperCase()];
            if (geocodedCity) addressParts.push(geocodedCity.toUpperCase());
            if (geocodedState || geocodedZip) {
              addressParts.push(`${geocodedState} ${geocodedZip}`.trim());
            }
            const formattedAddress = addressParts.join(", ");
            setPropertyAddress(formattedAddress);
          }
          
          if (pin) {
            setParcelPin(pin);
          }
        }
        
        const props = selectedParcel?.properties || {};
        const ownerName = props.ownname || props.owner || 'Unknown owner';
        const pin = props.parno || props.pin || props.parcel_id || props.reid || '';
        toast.success(`Property loaded: ${ownerName}${pin ? ` (PIN: ${pin})` : ''}`);
      } else {
        toast.info("No parcel data found for this location");
      }
    } catch (error) {
      logger.error("Failed to fetch parcel data:", error);
    } finally {
      setLoadingParcels(false);
    }
  }, [mapLoaded, setPropertyAddress, setParcelPin]);

  const initializeMap = useCallback(async () => {
    if (map.current || !MAPBOX_TOKEN) return;
    
    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    let center = [-78.6382, 35.7796];
    let zoom = 14;
    
    if (polygons.length > 0 && polygons[0].coordinates) {
      center = calculateCenter(polygons);
    } else if (propertyAddress) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(propertyAddress)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
        );
        const data = await response.json();
        if (data.features && data.features.length > 0) {
          center = data.features[0].center;
        }
      } catch (error) {
        logger.error("Geocoding failed:", error);
      }
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: center,
      zoom: zoom,
    });

    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: { polygon: true, line_string: true, trash: true },
      defaultMode: "simple_select",
      styles: [
        { id: "gl-draw-polygon-fill", type: "fill", filter: ["all", ["==", "$type", "Polygon"]], paint: { "fill-color": "#dc2626", "fill-opacity": 0.3 } },
        { id: "gl-draw-polygon-stroke", type: "line", filter: ["all", ["==", "$type", "Polygon"]], paint: { "line-color": "#dc2626", "line-width": 3 } },
        // Line-string styles for the trail draw mode (the in-progress line)
        { id: "gl-draw-line-active", type: "line", filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]], paint: { "line-color": "#f59e0b", "line-width": 3, "line-dasharray": [0.5, 1.5] } },
        { id: "gl-draw-line-inactive", type: "line", filter: ["all", ["==", "$type", "LineString"], ["==", "active", "false"]], paint: { "line-color": "#f59e0b", "line-width": 3 } },
        { id: "gl-draw-polygon-and-line-vertex-active", type: "circle", filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]], paint: { "circle-radius": 6, "circle-color": "#fbbf24" } },
        // Midpoint handles — appear on hover when a polygon is in direct_select mode (built-in MapboxDraw feature)
        { id: "gl-draw-polygon-and-line-vertex-midpoint", type: "circle", filter: ["all", ["==", "meta", "midpoint"], ["==", "$type", "Point"]], paint: { "circle-radius": 4, "circle-color": "#fff", "circle-stroke-color": "#dc2626", "circle-stroke-width": 2 } },
      ],
    });

    map.current.addControl(draw.current, "top-right");
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.current.on("load", () => {
      setMapLoaded(true);

      // Add USFWS National Wetlands Inventory layer
      map.current.addSource("nwi-wetlands", {
        type: "raster",
        tiles: [NWI_TILE_URL],
        tileSize: 256,
      });
      map.current.addLayer({
        id: "nwi-wetlands-layer",
        type: "raster",
        source: "nwi-wetlands",
        paint: { "raster-opacity": 0.6 },
      });
      
      if (polygons.length > 0) {
        polygons.forEach((poly, index) => {
          if (poly && poly.coordinates) {
            draw.current.add({
              type: "Feature",
              geometry: poly,
              properties: { areaIndex: index },
            });
          }
        });
        fitMapToPolygons();
      }
      
      if (propertyAddress) {
        fetchParcelData(propertyAddress);
      }
    });

    map.current.on("draw.create", handleDrawCreate);
    map.current.on("draw.update", handleDrawUpdate);
    map.current.on("draw.delete", handleDrawDelete);
  }, [polygons, propertyAddress, handleDrawCreate, handleDrawUpdate, handleDrawDelete, fitMapToPolygons, fetchParcelData]);

  // ---------- FREEHAND MODE (custom; Mapbox Draw has no native freehand) ----------
  const _freehandFinish = useCallback(() => {
    if (!map.current || !draw.current) return;
    const points = freehandRef.current.points;
    if (points.length < 3) {
      toast.error("Freehand area needs more points — keep your mouse down longer.");
    } else {
      // Close the ring
      const closed = [...points, points[0]];
      const feature = {
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [closed] },
      };
      const ids = draw.current.add(feature);
      // Emit a synthetic create event so existing handler runs
      handleDrawCreate({ features: [{ ...feature, id: ids[0] }] });
    }
    // Cleanup preview layer + listeners
    if (map.current.getLayer("freehand-preview-line")) map.current.removeLayer("freehand-preview-line");
    if (map.current.getSource(freehandRef.current.sourceId)) map.current.removeSource(freehandRef.current.sourceId);
    map.current.dragPan.enable();
    map.current.getCanvas().style.cursor = "";
    freehandRef.current.active = false;
    freehandRef.current.points = [];
  }, [handleDrawCreate]);

  const _freehandMouseMove = useCallback((e) => {
    if (!freehandRef.current.active || !map.current) return;
    const lng = e.lngLat.lng;
    const lat = e.lngLat.lat;
    const last = freehandRef.current.points[freehandRef.current.points.length - 1];
    if (last && Math.hypot(last[0] - lng, last[1] - lat) < 0.000005) return; // throttle very close points
    freehandRef.current.points.push([lng, lat]);
    const src = map.current.getSource(freehandRef.current.sourceId);
    if (src) {
      src.setData({
        type: "Feature",
        geometry: { type: "LineString", coordinates: freehandRef.current.points },
        properties: {},
      });
    }
  }, []);

  const _freehandMouseUp = useCallback(() => {
    if (!freehandRef.current.active) return;
    map.current.off("mousemove", _freehandMouseMove);
    map.current.off("mouseup", _freehandMouseUp);
    _freehandFinish();
  }, [_freehandMouseMove, _freehandFinish]);

  const _freehandMouseDown = useCallback((e) => {
    if (!map.current || drawModeRef.current !== DRAW_MODES.FREEHAND) return;
    e.preventDefault();
    map.current.dragPan.disable();
    map.current.getCanvas().style.cursor = "crosshair";
    freehandRef.current.active = true;
    freehandRef.current.points = [[e.lngLat.lng, e.lngLat.lat]];
    if (!map.current.getSource(freehandRef.current.sourceId)) {
      map.current.addSource(freehandRef.current.sourceId, {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "LineString", coordinates: [] }, properties: {} },
      });
      map.current.addLayer({
        id: "freehand-preview-line",
        type: "line",
        source: freehandRef.current.sourceId,
        paint: { "line-color": "#dc2626", "line-width": 3, "line-dasharray": [2, 1] },
      });
    }
    map.current.on("mousemove", _freehandMouseMove);
    map.current.once("mouseup", _freehandMouseUp);
  }, [_freehandMouseMove, _freehandMouseUp]);

  // ---------- FOLLOW PROPERTY LINE (mid-polygon-draw boundary snap) ----------
  //
  // While the user is drawing a polygon (MapboxDraw's `draw_polygon` mode), if the
  // followParcelLine toggle is ON, the very next click causes the segment from the
  // previous vertex to snap along the closest parcel boundary. There are always two
  // paths around the ring (shorter and longer) — we show both and the user picks.
  //
  // Implementation: we run our click listener AFTER MapboxDraw processes the click
  // (it fires the map's own click event). MapboxDraw has already added the just-
  // clicked vertex to the in-progress polygon. We then mutate the polygon's coords
  // to insert the boundary path between the previous vertex and this new one.

  const _getSelectedParcelRing = useCallback(() => {
    if (!map.current) return null;
    const src = map.current.getSource("selected-parcel");
    if (!src || !src._data) return null;
    const feature = src._data?.features?.[0];
    const geom = feature?.geometry;
    if (!geom) return null;
    // Prefer Polygon; if MultiPolygon, take the largest ring by point count
    if (geom.type === "Polygon") return geom.coordinates[0];
    if (geom.type === "MultiPolygon") {
      const rings = geom.coordinates.map((c) => c[0]).sort((a, b) => b.length - a.length);
      return rings[0];
    }
    return null;
  }, []);

  const _clearBoundaryPreviewLayers = useCallback(() => {
    if (!map.current) return;
    ["boundary-choice-a-line", "boundary-choice-b-line"].forEach((id) => {
      if (map.current.getLayer(id)) map.current.removeLayer(id);
    });
    ["boundary-choice-a", "boundary-choice-b"].forEach((id) => {
      if (map.current.getSource(id)) map.current.removeSource(id);
    });
  }, []);

  const _drawBoundaryPreview = useCallback((path, sourceId, color) => {
    if (!map.current) return;
    map.current.addSource(sourceId, {
      type: "geojson",
      data: { type: "Feature", geometry: { type: "LineString", coordinates: path }, properties: {} },
    });
    map.current.addLayer({
      id: `${sourceId}-line`,
      type: "line",
      source: sourceId,
      paint: { "line-color": color, "line-width": 5, "line-opacity": 0.85 },
    });
  }, []);

  // Given a closed ring and two arbitrary points (P1, P2), return the two paths that
  // trace the ring boundary between the nearest boundary points to P1 and P2.
  const _computeBoundaryPaths = useCallback((ring, p1, p2) => {
    if (!ring || ring.length < 4) return null;
    // Ensure ring is closed (first == last)
    const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      ? ring
      : [...ring, ring[0]];
    const ringLine = lineString(closed);
    const npA = nearestPointOnLine(ringLine, point(p1), { units: "kilometers" });
    const npB = nearestPointOnLine(ringLine, point(p2), { units: "kilometers" });
    const posA = npA.properties.location;
    const posB = npB.properties.location;

    // Precompute cumulative distance for each ring vertex — needed to decide which
    // vertices belong to the "arc from A to B" vs the "other arc".
    const cum = [0];
    for (let i = 1; i < closed.length; i++) {
      cum.push(cum[i - 1] + length(lineString([closed[i - 1], closed[i]]), { units: "kilometers" }));
    }

    // Slice 1: A → B along the ring in the natural direction
    const slice1 = lineSlice(npA, npB, ringLine).geometry.coordinates;

    // Slice 2: "the other way around". Vertices are split into a "tail" (cum > hi,
    // after the AB arc) and "head" (cum < lo, before the AB arc). Concat tail
    // then head — this walks the ring past posB, over the wrap-around, back to posA.
    const lo = Math.min(posA, posB);
    const hi = Math.max(posA, posB);
    const tail = [];
    const head = [];
    for (let i = 0; i < closed.length - 1; i++) {
      if (cum[i] > hi) tail.push(closed[i]);
      else if (cum[i] < lo) head.push(closed[i]);
    }
    const startPt = posA <= posB ? npB : npA;
    const endPt = posA <= posB ? npA : npB;
    const other = [startPt.geometry.coordinates, ...tail, ...head, endPt.geometry.coordinates];

    // Deduplicate consecutive identical points
    const dedup = (arr) => arr.filter((c, i) => i === 0 || c[0] !== arr[i - 1][0] || c[1] !== arr[i - 1][1]);
    // Ensure both paths run from P1-side to P2-side (posA → posB direction)
    const pathA = posA <= posB ? dedup(slice1) : dedup(slice1.slice().reverse());
    const pathB = posA <= posB ? dedup(other.slice().reverse()) : dedup(other);

    const lenAft = length(lineString(pathA), { units: "kilometers" }) * 3280.84;
    const lenBft = length(lineString(pathB), { units: "kilometers" }) * 3280.84;
    return {
      shorter: lenAft <= lenBft ? pathA : pathB,
      longer: lenAft <= lenBft ? pathB : pathA,
      pathA_length_ft: lenAft,
      pathB_length_ft: lenBft,
    };
  }, []);

  // Insert boundary vertices between the last-two clicks of the in-progress polygon.
  const _applyBoundaryPath = useCallback((chosenPath) => {
    if (!draw.current) return;
    const featureId = activePolygonIdRef.current;
    if (!featureId) return;
    const feature = draw.current.get(featureId);
    if (!feature || feature.geometry.type !== "Polygon") return;
    const ring = feature.geometry.coordinates[0];
    // Ring in draw_polygon mode is: [v0, v1, ..., vN, v0]  (auto-closed)
    // The last user-placed vertex is vN, previous is v(N-1). We insert path BETWEEN them.
    if (ring.length < 3) return;
    const closingVertex = ring[ring.length - 1];        // == ring[0]
    const lastClick = ring[ring.length - 2];
    const prevClick = ring[ring.length - 3];
    // Chosen path already goes prevClick → ...boundary... → lastClick
    // Strip endpoints of the path to avoid duplicating prevClick/lastClick vertices
    const interior = chosenPath.slice(1, -1);
    const newRing = [
      ...ring.slice(0, ring.length - 2),   // v0..v(N-1)  (includes prevClick)
      ...interior,
      lastClick,                            // vN
      closingVertex,                        // closing
    ];
    const updated = {
      ...feature,
      geometry: { type: "Polygon", coordinates: [newRing] },
    };
    draw.current.add(updated);
  }, []);

  const _followLineClickHandler = useCallback((e) => {
    // Only run when user is in polygon-draw mode AND toggle is on AND no dialog is up
    if (!map.current || !draw.current) return;
    if (drawModeRef.current !== DRAW_MODES.POLYGON) return;
    if (!followParcelLineRef.current) return;
    if (draw.current.getMode() !== "draw_polygon") return;
    if (boundaryChoice) return; // already choosing

    // Grab the in-progress polygon (MapboxDraw has just processed the click and
    // appended the new vertex).
    // Use a microtask to run after MapboxDraw internal handlers.
    setTimeout(() => {
      const all = draw.current.getAll();
      // The in-progress feature is the most recently added polygon with fewer vertices
      // than a closed one would need; simplest: pick the one that isn't in `polygons`.
      const savedIds = new Set(all.features.map((f) => f.id).filter((id) => {
        // MapboxDraw assigns numeric ids; saved polygons live in state and we can't
        // trivially match them here, so we fall back to: pick the polygon that has
        // the FEWEST vertices, since the in-progress one is being built.
        return true;
      }));
      const polys = all.features.filter((f) => f.geometry?.type === "Polygon");
      if (polys.length === 0) return;
      const activePoly = polys.reduce((least, f) => {
        const n = f.geometry.coordinates[0]?.length ?? 0;
        return !least || n < (least.geometry.coordinates[0]?.length ?? Infinity) ? f : least;
      }, null);
      if (!activePoly) return;
      activePolygonIdRef.current = activePoly.id;
      const ring = activePoly.geometry.coordinates[0];
      if (ring.length < 3) {
        // Need at least one previous vertex + the current click; draw_polygon has [v0, v1, v0]
        toast.info("Place one straight-line vertex first, then toggle Follow to snap the next segment.");
        setFollowParcelLine(false);
        return;
      }

      const parcelRing = _getSelectedParcelRing();
      if (!parcelRing) {
        toast.error("No parcel boundary loaded — enter a property address first.");
        setFollowParcelLine(false);
        return;
      }

      const prevClick = ring[ring.length - 3];   // vertex BEFORE the just-added click
      const lastClick = ring[ring.length - 2];   // the click that just happened
      const paths = _computeBoundaryPaths(parcelRing, prevClick, lastClick);
      if (!paths || !paths.shorter || paths.shorter.length < 2) {
        toast.error("Couldn't compute a boundary path — try clicking closer to the property line.");
        return;
      }

      // Draw both preview lines on the map
      _clearBoundaryPreviewLayers();
      _drawBoundaryPreview(paths.shorter, "boundary-choice-a", "#10b981"); // green
      _drawBoundaryPreview(paths.longer, "boundary-choice-b", "#f59e0b");  // amber

      // Open the choice picker
      setBoundaryChoice({
        shorter_length_ft: paths.pathA_length_ft <= paths.pathB_length_ft ? paths.pathA_length_ft : paths.pathB_length_ft,
        longer_length_ft: paths.pathA_length_ft <= paths.pathB_length_ft ? paths.pathB_length_ft : paths.pathA_length_ft,
        pickShorter: () => {
          _applyBoundaryPath(paths.shorter);
          _clearBoundaryPreviewLayers();
          setBoundaryChoice(null);
          setFollowParcelLine(false);
        },
        pickLonger: () => {
          _applyBoundaryPath(paths.longer);
          _clearBoundaryPreviewLayers();
          setBoundaryChoice(null);
          setFollowParcelLine(false);
        },
        cancel: () => {
          _clearBoundaryPreviewLayers();
          setBoundaryChoice(null);
        },
      });
    }, 0);
  }, [boundaryChoice, _getSelectedParcelRing, _computeBoundaryPaths, _applyBoundaryPath, _clearBoundaryPreviewLayers, _drawBoundaryPreview]);

  // Attach/detach the click listener only while in polygon-draw + toggle-on
  useEffect(() => {
    if (!map.current) return undefined;
    if (!followParcelLine) return undefined;
    if (drawMode !== DRAW_MODES.POLYGON) return undefined;
    map.current.on("click", _followLineClickHandler);
    return () => {
      if (map.current) map.current.off("click", _followLineClickHandler);
    };
  }, [followParcelLine, drawMode, _followLineClickHandler]);

  // ---------- PARCEL MODE (one-click: clicked parcel → designated area) ----------
  const _parcelClickHandler = useCallback((e) => {
    if (!map.current || drawModeRef.current !== DRAW_MODES.PARCEL) return;
    const layers = ["selected-parcel-fill", "neighbor-parcels-fill"].filter(
      (id) => !!map.current.getLayer(id)
    );
    if (layers.length === 0) {
      toast.error("No parcel data loaded for this property — try a different draw mode.");
      cancelParcelMode();
      return;
    }
    const features = map.current.queryRenderedFeatures(e.point, { layers });
    if (!features || features.length === 0) {
      // Allow the user to click again; don't tear down
      toast.info("Click directly on a parcel boundary (the blue/orange shaded areas).");
      return;
    }
    const f = features[0];
    if (!f.geometry || (f.geometry.type !== "Polygon" && f.geometry.type !== "MultiPolygon")) {
      toast.error("That feature isn't a polygon — pick another parcel.");
      return;
    }
    // queryRenderedFeatures clips by viewport; pull the full geometry from the original source
    const src = map.current.getSource(f.source);
    let fullGeom = f.geometry;
    try {
      const data = src && src._data;
      const origFeat = data?.features?.find?.((x) => x.id === f.id || x.properties?.pin === f.properties?.pin);
      if (origFeat?.geometry) fullGeom = origFeat.geometry;
    } catch { /* fall back to clipped */ }

    // Mapbox Draw doesn't accept MultiPolygon directly — flatten to first polygon
    const polygonGeom = fullGeom.type === "MultiPolygon"
      ? { type: "Polygon", coordinates: fullGeom.coordinates[0] }
      : fullGeom;

    if (draw.current) {
      const ids = draw.current.add({
        type: "Feature",
        properties: { __parcel: true, parcel_pin: f.properties?.pin },
        geometry: polygonGeom,
      });
      handleDrawCreate({ features: [{ geometry: polygonGeom, id: ids[0] }] });
    }
    // Tear down — single-click action
    map.current.off("click", _parcelClickHandler);
    map.current.getCanvas().style.cursor = "";
  }, [handleDrawCreate]);

  const cancelParcelMode = useCallback(() => {
    if (map.current) {
      map.current.off("click", _parcelClickHandler);
      map.current.getCanvas().style.cursor = "";
    }
    setIsDrawing(false);
  }, [_parcelClickHandler]);

  const startDrawing = useCallback((mode) => {
    if (!draw.current || !map.current) return;
    const targetMode = mode || drawModeRef.current || DRAW_MODES.POLYGON;
    setDrawMode(targetMode);
    drawModeRef.current = targetMode;
    setIsDrawing(true);

    if (targetMode === DRAW_MODES.POLYGON) {
      draw.current.changeMode("draw_polygon");
      toast.info("Click on the map to place polygon vertices. Double-click to finish.");
    } else if (targetMode === DRAW_MODES.TRAIL) {
      draw.current.changeMode("draw_line_string");
      toast.info(`Drawing trail at ${trailWidthRef.current} ft wide. Click to place points, double-click to finish.`);
    } else if (targetMode === DRAW_MODES.FREEHAND) {
      draw.current.changeMode("simple_select");
      map.current.once("mousedown", _freehandMouseDown);
      toast.info("Press and HOLD the mouse button, drag to draw an outline, then release.");
    } else if (targetMode === DRAW_MODES.PARCEL) {
      draw.current.changeMode("simple_select");
      const hasParcelLayer = ["selected-parcel-fill", "neighbor-parcels-fill"].some((id) =>
        map.current.getLayer(id)
      );
      if (!hasParcelLayer) {
        toast.error("Parcel data hasn't loaded yet. Enter a property address first or pick a different draw mode.");
        setIsDrawing(false);
        drawModeRef.current = DRAW_MODES.POLYGON;
        setDrawMode(DRAW_MODES.POLYGON);
        return;
      }
      map.current.getCanvas().style.cursor = "pointer";
      map.current.on("click", _parcelClickHandler);
      toast.info("Click any parcel on the map to use its boundary as the area.");
    }
  }, [_freehandMouseDown, _parcelClickHandler]);

  const cancelDrawing = useCallback(() => {
    if (draw.current) draw.current.changeMode("simple_select");
    // Tear down freehand listeners if active
    if (map.current && freehandRef.current.active) {
      map.current.off("mousemove", _freehandMouseMove);
      map.current.off("mouseup", _freehandMouseUp);
      if (map.current.getLayer("freehand-preview-line")) map.current.removeLayer("freehand-preview-line");
      if (map.current.getSource(freehandRef.current.sourceId)) map.current.removeSource(freehandRef.current.sourceId);
      map.current.dragPan.enable();
      map.current.getCanvas().style.cursor = "";
      freehandRef.current.active = false;
      freehandRef.current.points = [];
    }
    // Tear down parcel-click listener if any
    if (map.current) {
      map.current.off("click", _parcelClickHandler);
      map.current.getCanvas().style.cursor = "";
    }
    setIsDrawing(false);
  }, [_freehandMouseMove, _freehandMouseUp, _parcelClickHandler]);

  const toggleWetlands = useCallback(() => {
    if (!map.current || !map.current.getLayer("nwi-wetlands-layer")) return;
    const next = !showWetlands;
    map.current.setLayoutProperty("nwi-wetlands-layer", "visibility", next ? "visible" : "none");
    setShowWetlands(next);
  }, [showWetlands]);

  // Re-add custom layers after a style change
  const reloadCustomLayers = useCallback(() => {
    if (!map.current) return;

    // NWI Wetlands
    if (!map.current.getSource("nwi-wetlands")) {
      map.current.addSource("nwi-wetlands", { type: "raster", tiles: [NWI_TILE_URL], tileSize: 256 });
      map.current.addLayer({ id: "nwi-wetlands-layer", type: "raster", source: "nwi-wetlands", paint: { "raster-opacity": 0.6 } });
      if (!showWetlands) {
        map.current.setLayoutProperty("nwi-wetlands-layer", "visibility", "none");
      }
    }

    // Re-add existing polygons to draw control
    if (draw.current && polygons.length > 0) {
      polygons.forEach((poly, index) => {
        if (poly?.coordinates) {
          draw.current.add({ type: "Feature", geometry: poly, properties: { areaIndex: index } });
        }
      });
    }

    // Re-fetch parcels
    if (propertyAddress) {
      fetchParcelData(propertyAddress);
    }
  }, [showWetlands, polygons, propertyAddress, fetchParcelData]);

  const confirmAddArea = useCallback((addAnother) => {
    if (pendingPolygon) {
      const withId = { ...pendingPolygon, _id: pendingPolygon._id || `area-${crypto.randomUUID()}` };
      setPolygons(prev => [...prev, withId]);
    }
    setPendingPolygon(null);
    setShowAddAreaDialog(false);
    
    if (addAnother) {
      startDrawing();
    }
  }, [pendingPolygon, setPolygons, startDrawing]);

  const removePolygon = useCallback((index) => {
    if (draw.current) {
      const allFeatures = draw.current.getAll();
      if (allFeatures.features[index]) {
        draw.current.delete(allFeatures.features[index].id);
      }
    }
    setPolygons(prev => prev.filter((_, i) => i !== index));
    toast.info("Area removed");
  }, [setPolygons]);

  // Re-buffer a saved trail polygon with a new width. The original centerline is stored
  // in poly._meta.line, so we can re-create a clean buffered polygon at any width.
  const rebufferTrail = useCallback((index, newWidthFeet) => {
    const w = parseFloat(newWidthFeet);
    if (!Number.isFinite(w) || w <= 0) {
      toast.error("Trail width must be greater than 0");
      return;
    }
    setPolygons((prev) => {
      const target = prev[index];
      if (!target || !target._meta || target._meta.kind !== "trail" || !target._meta.line) {
        toast.error("This area is not a trail — cannot re-buffer.");
        return prev;
      }
      const newPoly = trailLineToPolygon({ type: "LineString", coordinates: target._meta.line }, w);
      if (!newPoly) return prev;
      newPoly._meta = { ...target._meta, width_ft: w };

      // Sync MapboxDraw layer: delete the old feature at this position, add the new one
      if (draw.current) {
        const allFeatures = draw.current.getAll();
        if (allFeatures.features[index]) {
          draw.current.delete(allFeatures.features[index].id);
        }
        draw.current.add({
          type: "Feature",
          properties: { __trail: true, __trail_width_ft: w },
          geometry: newPoly,
        });
      }
      toast.success(`Trail re-buffered at ${w} ft wide`);
      const next = [...prev];
      next[index] = newPoly;
      return next;
    });
  }, [setPolygons, trailLineToPolygon]);

  // Go to specific coordinates and show parcel boundaries
  const goToCoordinates = useCallback(async (lat, lon) => {
    if (!map.current) return;
    
    // Fly to the coordinates
    map.current.flyTo({
      center: [lon, lat],
      zoom: 17,
      duration: 1500
    });

    // Fetch parcel data for this location
    setLoadingParcels(true);
    try {
      // Use reverse geocoding to get an address
      const geoResponse = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      const geoData = await geoResponse.json();
      const address = geoData.features?.[0]?.place_name || `${lat}, ${lon}`;
      
      // Fetch parcels
      const response = await axios.post(`${API}/parcels/by-address`, { 
        address,
        lat,
        lon
      });
      const data = response.data;
      
      if (data.features && data.features.length > 0) {
        // Clear existing parcel layers
        if (map.current.getLayer("selected-parcel-fill")) map.current.removeLayer("selected-parcel-fill");
        if (map.current.getLayer("selected-parcel-line")) map.current.removeLayer("selected-parcel-line");
        if (map.current.getLayer("neighbor-parcels-fill")) map.current.removeLayer("neighbor-parcels-fill");
        if (map.current.getLayer("neighbor-parcels-line")) map.current.removeLayer("neighbor-parcels-line");
        if (map.current.getSource("selected-parcel")) map.current.removeSource("selected-parcel");
        if (map.current.getSource("neighbor-parcels")) map.current.removeSource("neighbor-parcels");
        
        // Show all parcels with property lines
        map.current.addSource("neighbor-parcels", {
          type: "geojson",
          data: { type: "FeatureCollection", features: data.features }
        });
        
        map.current.addLayer({
          id: "neighbor-parcels-fill",
          type: "fill",
          source: "neighbor-parcels",
          paint: { 
            "fill-color": "#4169E1", 
            "fill-opacity": 0.1 
          }
        });
        
        map.current.addLayer({
          id: "neighbor-parcels-line",
          type: "line",
          source: "neighbor-parcels",
          paint: { 
            "line-color": "#4169E1", 
            "line-width": 2.5,
            "line-opacity": 0.9
          }
        });
        
        toast.success(`Showing ${data.features.length} property boundaries. Use "Draw Area" to mark the clearing area.`);
      } else {
        toast.error("No parcels found at these coordinates");
      }
    } catch (error) {
      logger.error("Failed to fetch parcels at coordinates:", error);
      toast.error("Failed to load parcel data");
    } finally {
      setLoadingParcels(false);
    }
  }, []);

  return {
    mapContainer,
    map,
    draw,
    mapLoaded,
    isDrawing,
    showAddAreaDialog,
    setShowAddAreaDialog,
    pendingPolygon,
    loadingParcels,
    showWetlands,
    drawMode,
    setDrawMode,
    trailWidthFeet,
    setTrailWidthFeet,
    followParcelLine,
    setFollowParcelLine,
    boundaryChoice,
    initializeMap,
    startDrawing,
    cancelDrawing,
    toggleWetlands,
    reloadCustomLayers,
    confirmAddArea,
    removePolygon,
    rebufferTrail,
    fetchParcelData,
    fitMapToPolygons,
    goToCoordinates,
  };
};

export default useMapbox;
